import {
  CURRENT_TERMS_OF_SERVICE,
  UserTermsOfService,
  useTermsAcceptanceCheck,
} from "@yardzen/components/user-terms-of-service";
import {
  identifyLogRocketUser,
  useHubSpotIdentify,
} from "@yardzen/next-client-util";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { ReactNode } from "react";

interface SessionUser {
  id?: string;
  email?: string;
  mustAcceptTerms?: boolean;
}

interface MainContentProps {
  children: ReactNode;
}

export default function MainContent({ children }: MainContentProps) {
  const { data: session, status } = useSession();
  const user = session?.user as SessionUser | undefined;
  const userId = user?.id;

  // The Toll subtree owns its own (co-branded) terms gate below V2AuthProvider
  // (see TollTermsGate). Resolving the partner here — above that provider —
  // meant firing /auth/current-user before the v2 token existed, whose 401
  // bounced the user back to /login. So skip this generic gate on partner
  // routes and let the toll shell handle it where auth is guaranteed ready.
  const pathname = usePathname();
  const isPartnerRoute = Boolean(pathname?.startsWith("/toll"));

  // Identify LogRocket user when authenticated
  if (userId && user?.email) {
    identifyLogRocketUser(userId, user.email);
  }

  // Stitch this browser's HubSpot session to the authenticated user once per
  // session (guarded internally to avoid inflating page-view counts).
  useHubSpotIdentify(user?.email);

  // Check terms acceptance with a fresh API call to avoid cache issues.
  // This is needed because when users accept terms in another app and return
  // here, the session cache may still have stale mustAcceptTerms=true.
  const {
    hasAcceptedTerms,
    checkFailed: termsCheckFailed,
    loading: termsLoading,
  } = useTermsAcceptanceCheck(
    userId,
    status !== "authenticated", // Skip if not authenticated
  );

  // Only show terms modal if:
  // 1. User is authenticated
  // 2. Fresh API check confirms they haven't accepted terms
  // 3. The terms check has completed loading
  // 4. The check actually completed — a failed check means acceptance is
  //    unknown, not false, and prompting on it re-prompts users who already
  //    accepted (and cannot succeed when the failure is an auth one).
  const showTermsModal =
    status === "authenticated" &&
    userId &&
    !termsLoading &&
    !termsCheckFailed &&
    !hasAcceptedTerms &&
    !isPartnerRoute;

  if (showTermsModal) {
    return (
      <UserTermsOfService
        userId={userId}
        typeOfTos={CURRENT_TERMS_OF_SERVICE}
      />
    );
  }

  return (
    <div id="root">
      <div className="min-h-[calc(100%-56px)]">{children}</div>
    </div>
  );
}
